import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Zap, Sparkles, CalendarDays, ArrowUpRight } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';
import { useLanguage } from '../../features/language/useLanguage';

type DisplayTier = 'free' | 'basic' | 'plus';

const tierTheme: Record<DisplayTier, {
  icon: React.ComponentType<{ className?: string }>;
  pill: string;
  iconWrap: string;
  dot: string;
  tooltipAccent: string;
}> = {
  free: {
    icon: Lock,
    pill:
      'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 hover:border-slate-300',
    iconWrap: 'bg-slate-200/70 text-slate-500',
    dot: 'bg-slate-400',
    tooltipAccent: 'text-slate-500',
  },
  basic: {
    icon: Zap,
    pill:
      'border-sky-200 bg-gradient-to-r from-sky-50 to-cyan-50 text-sky-700 hover:from-sky-100 hover:to-cyan-100 hover:border-sky-300',
    iconWrap: 'bg-sky-500/10 text-sky-600',
    dot: 'bg-sky-500',
    tooltipAccent: 'text-sky-600',
  },
  plus: {
    icon: Sparkles,
    pill:
      'border-amber-300/70 bg-gradient-to-r from-amber-50 via-amber-50 to-orange-50 text-amber-800 hover:from-amber-100 hover:to-orange-100 hover:border-amber-400 shadow-[0_0_0_1px_rgba(251,191,36,0.15),0_4px_12px_-4px_rgba(217,119,6,0.25)]',
    iconWrap: 'bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-sm shadow-amber-500/40',
    dot: 'bg-amber-500',
    tooltipAccent: 'text-amber-600',
  },
};

const formatExpiryDate = (date: Date, locale: string): string =>
  date.toLocaleDateString(locale === 'id' ? 'id-ID' : 'en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

export const SubscriptionTierBadge: React.FC = () => {
  const { tier, expiresAt, daysRemaining, isLoading } = useSubscription();
  const { t, languageCode } = useLanguage();
  const [open, setOpen] = useState(false);

  if (isLoading) return null;

  // PRD 76: tier comes from the backend as 'free' | 'basic' | 'plus' directly.
  const displayTier: DisplayTier =
    tier === 'basic' ? 'basic' : tier === 'plus' ? 'plus' : 'free';

  const theme = tierTheme[displayTier];
  const Icon = theme.icon;
  const isPaid = displayTier !== 'free';
  const showTooltip = open && isPaid && expiresAt;
  const label = t(`subscription.tier.${displayTier}`);

  const tooltipTitle =
    displayTier === 'plus'
      ? t('subscription.tier.tooltip.title.plus')
      : t('subscription.tier.tooltip.title.basic');
  const expiryLabel = t('subscription.tier.tooltip.expiryLabel');
  const daysLabel = daysRemaining !== null ? t('subscription.tier.tooltip.daysLeft', { days: daysRemaining }) : '';
  const manageLabel = t('subscription.tier.tooltip.manage');
  const upgradeLabel = t('subscription.tier.tooltip.upgradeNow');

  return (
    <div
      className="relative ml-3"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      data-test-id="header-subscription-tier"
    >
      <Link
        to="/settings/subscription"
        className={`group inline-flex items-center gap-1.5 rounded-full border pl-1 pr-3 py-1 text-xs font-semibold tracking-wide transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-amber-400/50 ${theme.pill}`}
        data-test-id={`tier-pill-${displayTier}`}
      >
        <span
          className={`flex h-5 w-5 items-center justify-center rounded-full ${theme.iconWrap}`}
        >
          <Icon className="h-3 w-3" />
        </span>
        <span className="uppercase">{label}</span>
        {isPaid && daysRemaining !== null && daysRemaining <= 7 && (
          <span
            className={`ml-0.5 inline-block h-1.5 w-1.5 animate-pulse rounded-full ${theme.dot}`}
            aria-hidden="true"
          />
        )}
      </Link>

      {/* Tooltip — paid tiers only, on hover */}
      {showTooltip && expiresAt && (
        <div
          className="absolute right-0 top-full z-50 mt-2 w-64 origin-top-right"
          role="tooltip"
          data-test-id="tier-tooltip"
          style={{ animation: 'tierTooltipIn 150ms ease-out' }}
        >
          {/* Tail */}
          <div className="absolute -top-1.5 right-6 h-3 w-3 rotate-45 border-l border-t border-gray-200 bg-white" />

          <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl shadow-gray-900/10">
            {/* Subtle accent bar */}
            <div
              className={`h-0.5 w-full ${
                displayTier === 'plus'
                  ? 'bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400'
                  : 'bg-gradient-to-r from-sky-400 to-cyan-400'
              }`}
            />

            <div className="px-4 py-3">
              <div className="flex items-baseline justify-between">
                <p className={`text-[11px] font-bold uppercase tracking-[0.12em] ${theme.tooltipAccent}`}>
                  {tooltipTitle}
                </p>
                {daysRemaining !== null && (
                  <span className="text-[11px] font-medium text-gray-400">
                    {daysLabel}
                  </span>
                )}
              </div>

              <div className="mt-2 flex items-start gap-2">
                <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                <div className="min-w-0">
                  <p className="text-[10px] font-medium uppercase tracking-wider text-gray-400">
                    {expiryLabel}
                  </p>
                  <p
                    className="mt-0.5 text-sm font-semibold text-gray-900"
                    data-test-id="tier-tooltip-expires-at"
                  >
                    {formatExpiryDate(expiresAt, languageCode)}
                  </p>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-2.5">
                <span className="text-[11px] text-gray-500">
                  {manageLabel}
                </span>
                <ArrowUpRight className="h-3.5 w-3.5 text-gray-400 transition-transform group-hover:translate-x-0.5" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Free tier tooltip — minimal upsell hint */}
      {open && !isPaid && (
        <div
          className="absolute right-0 top-full z-50 mt-2 w-56"
          role="tooltip"
          data-test-id="tier-tooltip-free"
          style={{ animation: 'tierTooltipIn 150ms ease-out' }}
        >
          <div className="absolute -top-1.5 right-6 h-3 w-3 rotate-45 border-l border-t border-gray-200 bg-white" />
          <div className="rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 shadow-xl shadow-gray-900/10">
            <p className="text-[11px] font-medium text-gray-500">
              {t('subscription.tier.tooltip.freeHint')}
            </p>
            <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-amber-700">
              {upgradeLabel}
              <ArrowUpRight className="h-3 w-3" />
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
